import React, { useState } from 'react';
import {
    Box, Typography, Paper, TextField, Button, Grid,
    MenuItem, CircularProgress, Snackbar, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Fade, Zoom
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ScienceIcon from '@mui/icons-material/Science';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import API_URL from '../config';

// Soil / Rock type options
const SOIL_ROCK_TYPES = [
    'Clay', 'Silt', 'Sand', 'Gravel', 'Silty Clay', 'Clayey Sand',
    'Sandy Gravel', 'Weathered Rock', 'Limestone', 'Sandstone',
    'Granite', 'Basalt', 'Mudstone', 'Marl', 'Chalk', 'Other'
];

function Boreholes() {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    // Form state
    const [form, setForm] = useState({
        project_name: '',
        borehole_no: '',
        latitude: '',
        longitude: '',
        depth_m: '',
        is_50_mpa: '',
        soil_rock_type: '',
        geological_description: ''
    });

    // Result / UI state
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);       // raw response body
    const [tableRows, setTableRows] = useState([]);   // normalized table analysis rows
    const [predictionRecords, setPredictionRecords] = useState([]); // raw predictions array
    const [predictionColumns, setPredictionColumns] = useState([]); // column keys
    const [interpretation, setInterpretation] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const showSnack = (message, severity = 'info') => setSnackbar({ open: true, message, severity });
    const closeSnack = () => setSnackbar((s) => ({ ...s, open: false }));

    const handleChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    // Normalize the AI response into a "table analysis" + "engineering interpretation".
    // The external API returns:
    //   { input: {...}, predictions: [{target, variant, model_path, prediction}, ...], summary: "..." }
    const normalizeResult = (data) => {
        const rows = [];
        let interp = '';
        let records = [];
        let columns = [];

        const pushRow = (key, value) => {
            if (value !== undefined && value !== null && value !== '') {
                rows.push({ key, value });
            }
        };

        if (data) {
            // 1) Predictions (array of records) -> proper table columns.
            if (Array.isArray(data.predictions) && data.predictions.length > 0) {
                records = data.predictions.filter((r) => r && typeof r === 'object');
                if (records.length > 0) {
                    // Explicit display order: target, variant, prediction.
                    // Any other keys (other than model_path) are appended after.
                    const preferred = ['target', 'variant', 'prediction'];
                    const keys = [];
                    records.forEach((r) => {
                        Object.keys(r).forEach((k) => {
                            if (k === 'model_path') return;   // not needed in the display
                            if (!keys.includes(k)) keys.push(k);
                        });
                    });
                    keys.sort((a, b) => {
                        const ia = preferred.indexOf(a);
                        const ib = preferred.indexOf(b);
                        if (ia === -1 && ib === -1) return 0;
                        if (ia === -1) return 1;
                        if (ib === -1) return -1;
                        return ia - ib;
                    });
                    columns = keys;
                }
            }

            // Extract the engineering interpretation text. The summary may be a
            // plain string or (from LangChain) an object exposing a `.content` /
            // `text` / `message` field — normalize it to a clean string here.
            const rawInterp =
                data.summary
                || data.engineering_interpretation
                || data.interpretation
                || data.engineering_interpretation_text
                || data.analysis
                || '';

            const toText = (v) => {
                if (v === null || v === undefined) return '';
                if (typeof v === 'string') return v;
                if (Array.isArray(v)) return v.map(toText).join('\n');
                if (typeof v === 'object') {
                    return v.content ?? v.text ?? v.message ?? v.answer ?? JSON.stringify(v);
                }
                return String(v);
            };

            interp = toText(rawInterp);

            // 2) table_analysis (alternative shapes) -> generic key/value rows.
            if (records.length === 0 && Array.isArray(data.table_analysis)) {
                data.table_analysis.forEach((item) => {
                    if (item && typeof item === 'object') {
                        Object.entries(item).forEach(([k, v]) => pushRow(k, v));
                    } else {
                        pushRow('Result', item);
                    }
                });
            } else if (records.length === 0 && data.table_analysis && typeof data.table_analysis === 'object') {
                Object.entries(data.table_analysis).forEach(([k, v]) => pushRow(k, v));
            }

            // 3) Flat response of key/values (excluding special fields).
            if (records.length === 0 && rows.length === 0 && typeof data === 'object') {
                const knownKeys = [
                    'engineering_interpretation', 'interpretation',
                    'engineering_interpretation_text', 'analysis',
                    'table_analysis', 'summary', 'predictions', 'input',
                    'message', 'status', 'error'
                ];
                Object.entries(data).forEach(([k, v]) => {
                    if (knownKeys.includes(k)) return;
                    if (v && typeof v === 'object') return;
                    pushRow(k, v);
                });
            }

            if (!interp && data.message) interp = data.message;
        }

        return { rows, interp, records, columns };
    };

    const handleRun = async () => {
        const required = [
            'project_name', 'borehole_no', 'latitude', 'longitude',
            'depth_m', 'soil_rock_type', 'geological_description'
        ];
        const missing = required.filter((f) => !String(form[f] || '').trim());
        if (missing.length > 0) {
            showSnack(`Please fill in: ${missing.map((f) => f.replace(/_/g, ' ')).join(', ')}`, 'warning');
            return;
        }

        setRunning(true);
        setResult(null);
        setTableRows([]);
        setPredictionRecords([]);
        setPredictionColumns([]);
        setInterpretation('');

        const toNum = (v) => {
            const n = parseFloat(v);
            return Number.isFinite(n) ? n : null;
        };

        const payload = {
            project_name: form.project_name.trim(),
            borehole_no: form.borehole_no.trim(),
            latitude: toNum(form.latitude),
            longitude: toNum(form.longitude),
            depth: toNum(form.depth_m),
            type: form.soil_rock_type,
            description: form.geological_description.trim(),
            is50: form.is_50_mpa.trim() === '' ? null : toNum(form.is_50_mpa)
        };

        try {
            const res = await axios.post(
                `${API_URL}/ai-reports/predict_boreholes`,
                payload,
                { headers: { ...headers, 'Content-Type': 'application/json' } }
            );

            const data = res.data;
            setResult(data);
            const { rows, interp, records, columns } = normalizeResult(data);
            setTableRows(rows);
            setPredictionRecords(records);
            setPredictionColumns(columns);
            setInterpretation(interp);
            showSnack('Boreholes prediction completed successfully.', 'success');

            try {
                await axios.post(`${API_URL}/logs`, {
                    module: 'Boreholes', action: 'RUN',
                    entity_id: form.borehole_no.trim(),
                    details: `Ran boreholes AI prediction for ${form.project_name}`,
                    source: 'Web'
                }, { headers });
            } catch (_) {}
        } catch (err) {
            let errorMsg = 'Failed to run prediction.';
            if (err.response?.data) {
                errorMsg = err.response.data.message || err.response.data.error || errorMsg;
            }
            showSnack(errorMsg, 'error');
        } finally {
            setRunning(false);
        }
    };

    const handleReset = () => {
        setForm({
            project_name: '', borehole_no: '', latitude: '', longitude: '',
            depth_m: '', is_50_mpa: '', soil_rock_type: '', geological_description: ''
        });
        setResult(null);
        setTableRows([]);
        setPredictionRecords([]);
        setPredictionColumns([]);
        setInterpretation('');
    };

    const hasResult = result !== null && (tableRows.length > 0 || predictionRecords.length > 0 || interpretation);

    const interpretationParagraphs = interpretation ? interpretation.split(/\n\s*\n/).filter((p) => p.trim().length > 0) : [];

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', px: { xs: 1, md: 3 }, pb: 6, bgcolor: '#f5f7fa', boxSizing: 'border-box' }}>
            {/* Top banner */}
            <Zoom in={true} timeout={600}>
                <Box sx={{
                    mt: 2, mb: 3, p: { xs: 2.5, md: 4 }, borderRadius: 3,
                    background: 'linear-gradient(120deg, #1e3c72 0%, #2a5298 45%, #1d4ed8 100%)',
                    color: 'white', position: 'relative', overflow: 'hidden',
                    boxShadow: '0 12px 32px rgba(30, 60, 114, 0.28)',
                }}>
                    <Box sx={{ position: 'absolute', top: -70, right: -30, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', pointerEvents: 'none' }} />
                    <Box sx={{ position: 'absolute', bottom: -90, right: '25%', width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
                    <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
                        <Box>
                            <Chip label="AI PREDICTION" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 800, fontSize: '0.68rem', letterSpacing: 1, mb: 1, height: 22 }} />
                            <Typography variant="h5" fontWeight="800" sx={{ letterSpacing: 0.3, display: 'flex', alignItems: 'center', gap: 1.5, color: 'white' }}>
                                <ScienceIcon sx={{ fontSize: 30 }} />
                                Boreholes Intelligence
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5, maxWidth: 560 }}>
                                Predict subsurface properties and engineering parameters from borehole logs with AI.
                            </Typography>
                        </Box>
                        <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleReset}
                            sx={{ borderRadius: 2, fontWeight: 700, px: 2.5, py: 1, bgcolor: 'rgba(255,255,255,0.18)', color: 'white', textTransform: 'none', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.25)', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' } }}>
                            Reset
                        </Button>
                    </Box>
                </Box>
            </Zoom>

            {/* Two-column layout */}
            <Grid container spacing={3} alignItems="flex-start">
                {/* Left: Input form */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 2px 10px rgba(15, 23, 42, 0.05)', overflow: 'hidden' }}>
                        <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #eef2f7', background: 'linear-gradient(90deg, #eff6ff 0%, #dbeafe 100%)' }}>
                            <Typography variant="subtitle1" fontWeight={800} color="#1e3c72" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ScienceIcon fontSize="small" /> Input Data
                            </Typography>
                            <Typography variant="caption" color="#6b7280">Fill in the borehole parameters below.</Typography>
                        </Box>
                        <Box sx={{ p: 3 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Project Name" value={form.project_name}
                                        onChange={handleChange('project_name')} disabled={running}
                                        placeholder="e.g. Al Khobar Site" size="small" InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Borehole No" value={form.borehole_no}
                                        onChange={handleChange('borehole_no')} disabled={running}
                                        placeholder="BH-01" size="small" InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Depth (m)" type="number" value={form.depth_m}
                                        onChange={handleChange('depth_m')} disabled={running}
                                        placeholder="20" size="small" inputProps={{ step: 'any', min: 0 }} InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Latitude" type="number" value={form.latitude}
                                        onChange={handleChange('latitude')} disabled={running}
                                        placeholder="26.2942" size="small" inputProps={{ step: 'any' }} InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Longitude" type="number" value={form.longitude}
                                        onChange={handleChange('longitude')} disabled={running}
                                        placeholder="50.1989" size="small" inputProps={{ step: 'any' }} InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth select label="Soil / Rock Type" value={form.soil_rock_type}
                                        onChange={handleChange('soil_rock_type')} disabled={running} size="small" InputLabelProps={{ shrink: true }}>
                                        {SOIL_ROCK_TYPES.map((t) => (
                                            <MenuItem key={t} value={t}>{t}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Is 50 (MPa)" type="number" value={form.is_50_mpa}
                                        onChange={handleChange('is_50_mpa')} disabled={running}
                                        placeholder="50" size="small" inputProps={{ step: 'any' }} InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Geological Description" value={form.geological_description}
                                        onChange={handleChange('geological_description')} disabled={running}
                                        placeholder="Describe the geological conditions..."
                                        multiline minRows={3} size="small" InputLabelProps={{ shrink: true }} />
                                </Grid>
                            </Grid>

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={handleRun}
                                disabled={running}
                                startIcon={running ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                                sx={{
                                    mt: 3, textTransform: 'none', fontWeight: 800, borderRadius: 2, py: 1.4, fontSize: '0.95rem',
                                    background: 'linear-gradient(120deg, #1e3c72, #1d4ed8)',
                                    boxShadow: '0 10px 22px rgba(37, 99, 235, 0.35)',
                                    '&:hover': { boxShadow: '0 14px 28px rgba(37, 99, 235, 0.45)', filter: 'brightness(1.02)' },
                                    '&:disabled': { bgcolor: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' }
                                }}>
                                {running ? 'Running...' : 'Run Prediction'}
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* Right: Results */}
                <Grid item xs={12} md={7}>
                    {running && (
                        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 2px 10px rgba(15,23,42,0.05)', p: 6, textAlign: 'center' }}>
                            <CircularProgress size={44} sx={{ color: '#2563eb', mb: 2 }} />
                            <Typography variant="body1" fontWeight={700} color="#334155">Running boreholes prediction…</Typography>
                            <Typography variant="caption" color="#94a3b8">This may take a moment</Typography>
                        </Paper>
                    )}

                    {!running && !hasResult && (
                        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px dashed #d1d5db', boxShadow: 'none', p: 6, textAlign: 'center', minHeight: { md: 420 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <ScienceIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                            <Typography variant="h6" fontWeight={700} color="#475569">No results yet</Typography>
                            <Typography variant="body2" color="#94a3b8" sx={{ maxWidth: 320 }}>
                                Fill in the borehole data on the left and press "Run Prediction" to see the analysis here.
                            </Typography>
                        </Paper>
                    )}

            {!running && hasResult && (
                <Fade in={true} timeout={500}>
                    <Box>
                        {/* Table Analysis */}
                        {(predictionRecords.length > 0 || tableRows.length > 0) && (
                            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 2px 10px rgba(15,23,42,0.05)', mb: 3, overflow: 'hidden' }}>
                                <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #eef2f7', display: 'flex', alignItems: 'center', gap: 1.5, background: 'linear-gradient(90deg, #eff6ff 0%, #dbeafe 100%)' }}>
                                    <AutoAwesomeIcon sx={{ color: '#1e3c72' }} />
                                    <Typography variant="subtitle1" fontWeight={800} color="#1e3c72">Table Analysis</Typography>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        {predictionRecords.length > 0 ? (
                                            <>
                                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                                    <TableRow>
                                                        {predictionColumns.map((col) => (
                                                            <TableCell key={col} sx={{ fontWeight: 700, color: '#1e3c72', textTransform: 'capitalize' }}>
                                                                {String(col).replace(/_/g, ' ')}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {predictionRecords.map((record, idx) => (
                                                        <TableRow key={idx} hover sx={{ '& td': { borderBottom: '1px solid #e5e7eb' } }}>
                                                            {predictionColumns.map((col) => (
                                                                <TableCell key={col} sx={{ color: '#334155' }}>
                                                                    {typeof record[col] === 'object'
                                                                        ? JSON.stringify(record[col])
                                                                        : String(record[col] ?? '')}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </>
                                        ) : (
                                            <>
                                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700, color: '#1e3c72' }}>Parameter</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, color: '#1e3c72' }}>Value / Analysis</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {tableRows.map((row, idx) => (
                                                        <TableRow key={idx} hover sx={{ '& td': { borderBottom: '1px solid #e5e7eb' } }}>
                                                            <TableCell sx={{ fontWeight: 600, color: '#1e3c72', whiteSpace: 'nowrap' }}>
                                                                {String(row.key).replace(/_/g, ' ')}
                                                            </TableCell>
                                                            <TableCell sx={{ color: '#334155' }}>
                                                                {typeof row.value === 'object'
                                                                    ? JSON.stringify(row.value)
                                                                    : String(row.value)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </>
                                        )}
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}

                        {/* Engineering Interpretation */}
                        {interpretation && (
                            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 2px 10px rgba(15,23,42,0.05)', overflow: 'hidden' }}>
                                <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #eef2f7', display: 'flex', alignItems: 'center', gap: 1.5, background: 'linear-gradient(90deg, #eff6ff 0%, #dbeafe 100%)' }}>
                                    <Chip label="Engineering" size="small" sx={{ bgcolor: '#dbeafe', color: '#1e3c72', fontWeight: 700 }} />
                                    <Typography variant="subtitle1" fontWeight={800} color="#1e3c72">Interpretation</Typography>
                                </Box>
                                <Box sx={{ px: 3, py: 2.5 }}>
                                    {interpretationParagraphs.map((paragraph, i) => (
                                        <Typography
                                            key={i}
                                            variant="body2"
                                            sx={{ color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-wrap', mb: i < interpretationParagraphs.length - 1 ? 1.5 : 0 }}
                                        >
                                            {paragraph.trim()}
                                        </Typography>
                                    ))}
                                </Box>
                            </Paper>
                        )}

                        {/* Raw response fallback (no parsable fields) */}
                        {tableRows.length === 0 && predictionRecords.length === 0 && !interpretation && (
                            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e5e7eb', p: 3 }}>
                                <Typography variant="body2" sx={{ color: '#475569', whiteSpace: 'pre-wrap' }}>
                                    {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                </Fade>
            )}
                </Grid>
            </Grid>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={closeSnack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={closeSnack} severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2.5, fontWeight: 700 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Boreholes;
